import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import faceRecognitionService from '../services/faceRecognitionService';
import axiosInstance from '../api/axiosInstance';

/**
 * Dialog to set a person as a reference for face recognition
 */
const ReferencePersonDialog = ({ open, onClose, sessionId, image, onSuccess }) => {
  const [personName, setPersonName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [imageLoading, setImageLoading] = useState(true);

  // Fetch the thumbnail image with authentication when dialog opens
  useEffect(() => {
    const fetchThumbnail = async () => {
      if (image && open) {
        try {
          setImageLoading(true);
          // Create a blob URL from the authenticated response
          const response = await axiosInstance.get(
            `/images/${image.uniqueId}/thumbnail`,
            { responseType: 'blob' }
          );
          // Create a blob URL for the image
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const url = URL.createObjectURL(blob);
          setThumbnailUrl(url);
          setImageLoading(false);
        } catch (err) {
          console.error('Error fetching image thumbnail:', err);
          setImageLoading(false);
          setError('Failed to load image thumbnail');
        }
      }
    };

    fetchThumbnail();

    // Clean up blob URL when dialog closes
    return () => {
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [image, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!personName || personName.trim() === '') {
      setError('Please enter a name for this person');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await faceRecognitionService.setReferencePerson(sessionId, image.uniqueId, personName);
      setSuccess(true);
      setLoading(false);
      
      // If successful, callback to parent with result
      if (onSuccess) {
        onSuccess(result);
      }
      
      // Close dialog after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || 'Failed to set reference person');
    }
  };

  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <PersonAddIcon sx={{ mr: 1 }} />
          Tag Person for Recognition
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {image && (
          <Box mb={3} display="flex" flexDirection="column" alignItems="center">
            {imageLoading ? (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                sx={{ 
                  width: '100%', 
                  height: '200px', 
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : thumbnailUrl ? (
              <img 
                src={thumbnailUrl} 
                alt={image.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '200px',
                  objectFit: 'contain',
                  borderRadius: '4px'
                }} 
              />
            ) : (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                sx={{ 
                  width: '100%', 
                  height: '200px', 
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }}
              >
                <Typography color="text.secondary">Failed to load image</Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" mt={1}>
              Image: {image.name}
            </Typography>
          </Box>
        )}
        
        <Typography variant="body1" mb={2}>
          Set this image as a reference person to find them across all photos in this session.
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <TextField
            autoFocus
            margin="dense"
            id="personName"
            label="Person's Name"
            fullWidth
            variant="outlined"
            value={personName}
            onChange={(e) => setPersonName(e.target.value)}
            disabled={loading || success}
            required
            helperText="Name should not contain spaces for AWS Rekognition compatibility"
          />
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Reference person set successfully!
            </Alert>
          )}
        </form>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || success}
          startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
        >
          {loading ? 'Setting...' : 'Set Reference Person'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReferencePersonDialog;
